import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Calendar, MapPin, Ticket, ArrowLeft, ExternalLink, Phone, Sparkles, Rocket } from "lucide-react";
import Header from "@/components/Header";
import OtpDialog from "@/components/OtpDialog";
import ImageGallery from "@/components/ImageGallery";
import SponsorDialog from "@/components/SponsorDialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { eventApi, api } from "@/lib/api";
import { formatDateLong } from "@/lib/format";
import { setSEO } from "@/lib/seo";
import { toast } from "sonner";

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otpOpen, setOtpOpen] = useState(false);
  const [sponsorOpen, setSponsorOpen] = useState(false);
  const [params] = useSearchParams();
  const [verifiedPhone, setVerifiedPhone] = useState(
    localStorage.getItem("verified_phone") || ""
  );

  useEffect(() => {
    eventApi.get(id).then(setEvent).catch(() => setEvent(null)).finally(() => setLoading(false));
  }, [id]);

  // Log a visit whenever a verified user opens an event page
  useEffect(() => {
    const phone = localStorage.getItem("verified_phone");
    if (!phone || !id) return;
    api.post(`/events/${id}/visit`, { phone }).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (params.get("sponsor") === "cancel") {
      toast.info("Sponsorship checkout was cancelled");
    }
  }, [params]);

  // Dynamic SEO — updates title, meta tags, OG cards and JSON-LD Event schema
  useEffect(() => {
    if (!event) return;
    const cityLabel = event.city_key ? event.city_key.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";
    const priceLabel = event.is_free || event.price === 0 ? "Free entry" : `Tickets from ₹${event.price}`;
    const dateLabel = event.event_date ? formatDateLong(event.event_date) : "";
    const rawDesc = (event.description || "").replace(/\s+/g, " ").trim();
    const shortDesc = rawDesc.length > 155 ? rawDesc.slice(0, 152) + "…" : rawDesc;
    const description =
      shortDesc ||
      `${event.title} at ${event.venue || cityLabel}${dateLabel ? " on " + dateLabel : ""}. ${priceLabel}. Book tickets on Fanpit.Club.`;

    const keywords = [
      event.title,
      cityLabel,
      event.venue,
      ...(event.tags || []),
      "events",
      "tickets",
      "Fanpit.Club",
    ].filter(Boolean).join(", ");

    const title = `${event.title}${cityLabel ? " · " + cityLabel : ""} | Fanpit.Club`;
    const image = event.image_url || (event.images && event.images[0]) || "";
    const url = typeof window !== "undefined" ? window.location.href : "";

    // Build JSON-LD Event schema for rich search results
    let startDate = null;
    if (event.event_date) {
      // Attempt "YYYY-MM-DDTHH:MM" combining event_time when parseable
      let iso = event.event_date;
      const t = (event.event_time || "").trim();
      const m = t.match(/^(\d{1,2}):?(\d{2})?\s*([AaPp][Mm])?$/);
      if (m) {
        let h = parseInt(m[1], 10);
        const mm = m[2] ? parseInt(m[2], 10) : 0;
        const ap = (m[3] || "").toLowerCase();
        if (ap === "pm" && h < 12) h += 12;
        if (ap === "am" && h === 12) h = 0;
        iso = `${event.event_date}T${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
      }
      startDate = iso;
    }

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: event.title,
      description,
      ...(startDate ? { startDate } : {}),
      ...(event.end_date ? { endDate: event.end_date } : {}),
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      image: image ? [image] : undefined,
      location: {
        "@type": "Place",
        name: event.venue || cityLabel || "Venue",
        address: event.address || cityLabel || "",
      },
      offers: {
        "@type": "Offer",
        price: event.is_free ? 0 : event.price,
        priceCurrency: "INR",
        availability: "https://schema.org/InStock",
        url,
        ...(event.booking_url ? { url: event.booking_url } : {}),
      },
      organizer: {
        "@type": "Organization",
        name: "Fanpit.Club",
        url: typeof window !== "undefined" ? window.location.origin : "https://fanpit.club",
      },
    };

    setSEO({ title, description, image, url, type: "event", keywords, jsonLd });
  }, [event]);

  const handleVerified = (phone) => {
    setVerifiedPhone(phone);
    localStorage.setItem("verified_phone", phone);
  };

  const startBooking = () => {
    if (verifiedPhone) return; // methods will show automatically
    setOtpOpen(true);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Header />
      <div className="pt-32 max-w-7xl mx-auto px-4 animate-pulse">
        <div className="h-96 bg-white rounded-3xl" />
      </div>
    </div>
  );

  if (!event) return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Header />
      <div className="pt-32 max-w-7xl mx-auto px-4 text-center">
        <h1 className="font-display text-4xl font-black">Event not found</h1>
        <Link to="/" className="text-[#FA003F] mt-4 inline-block font-bold">← Back to home</Link>
      </div>
    </div>
  );

  const priceLabel = event.is_free || event.price === 0 ? "Free" : `₹ ${event.price}`;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Header />
      <main className="pt-24 md:pt-28 pb-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-600 hover:text-[#FA003F] mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> Back to events
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Left column */}
            <div className="lg:col-span-8 space-y-10">
              <ImageGallery
                images={(event.images && event.images.length > 0) ? event.images : (event.image_url ? [event.image_url] : [])}
                alt={event.title}
              />

              <div className="space-y-4">
                <div className="flex flex-wrap gap-2 items-center" data-testid="event-tags">
                  {event.is_expired && (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 rounded-full px-3 py-1 text-xs font-black uppercase tracking-widest">
                      Booking closed
                    </Badge>
                  )}
                  {event.is_sponsored && (
                    <Badge className="bg-[#FA003F] text-white hover:bg-[#FA003F] rounded-full px-3 py-1 text-xs font-black uppercase tracking-widest inline-flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> Sponsored
                    </Badge>
                  )}
                  {event.tags?.map((t) => (
                    <Badge key={t} variant="secondary" className="bg-[#FA003F]/10 text-[#FA003F] hover:bg-[#FA003F]/15 rounded-full px-3 py-1 text-xs font-bold">
                      {t}
                    </Badge>
                  ))}
                </div>
                <h1 data-testid="event-title" className="font-display text-4xl sm:text-5xl font-black leading-[1.05] tracking-tight">
                  {event.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                  <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4 text-[#FA003F]" /> {formatDateLong(event.event_date)} · {event.event_time}</span>
                  <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#FA003F]" /> {event.venue}</span>
                </div>
              </div>

              <div className="prose max-w-none">
                <p className="whitespace-pre-line text-[15px] leading-relaxed text-gray-700">
                  {event.description}
                </p>
              </div>

              {event.faqs?.length > 0 && (
                <div>
                  <h3 className="font-display text-2xl font-black mb-4">Frequently Asked Questions</h3>
                  <Accordion type="single" collapsible className="bg-white rounded-2xl border border-black/10 divide-y divide-black/5">
                    {event.faqs.map((f, i) => (
                      <AccordionItem value={`f-${i}`} key={i} className="border-none">
                        <AccordionTrigger className="px-5 py-4 hover:no-underline font-bold text-left">{f.q}</AccordionTrigger>
                        <AccordionContent className="px-5 pb-4 text-gray-600">{f.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
            </div>

            {/* Right column - booking sidebar */}
            <aside className="lg:col-span-4">
              <div className="sticky top-28 space-y-4">
                <div className="bg-white rounded-3xl p-6 border border-black/10 shadow-[0_20px_50px_rgba(0,0,0,0.05)]">
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Date & Time</div>
                  <div className="mt-1 font-display text-lg font-black">{formatDateLong(event.event_date)}</div>
                  <div className="text-gray-600 text-sm">{event.event_time}</div>

                  <div className="mt-5 text-xs font-bold uppercase tracking-widest text-gray-500">Venue</div>
                  <div className="mt-1 font-display text-lg font-black">{event.venue}</div>
                  {event.address && <div className="text-gray-600 text-sm mt-0.5">{event.address}</div>}

                  <div className="mt-5 border-t border-black/5 pt-5 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Price</div>
                      <div data-testid="event-price" className="font-display text-3xl font-black text-[#FA003F] mt-1">
                        {priceLabel}
                      </div>
                    </div>
                    <Ticket className="h-8 w-8 text-[#FA003F]/30" />
                  </div>

                  {event.is_expired ? (
                    <div data-testid="booking-closed" className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-center">
                      <div className="text-xs font-black uppercase tracking-widest text-amber-700">Booking closed</div>
                      <div className="text-sm text-amber-900 mt-1">This event has already taken place.</div>
                    </div>
                  ) : !verifiedPhone ? (
                    <Button
                      data-testid="book-ticket-btn"
                      onClick={startBooking}
                      className="w-full mt-6 h-12 rounded-full bg-[#FA003F] hover:bg-[#C40031] text-base font-bold"
                    >
                      Book Ticket
                    </Button>
                  ) : (
                    <div className="mt-6 space-y-3" data-testid="booking-methods">
                      <div className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 font-semibold">
                        Verified as {verifiedPhone}
                      </div>
                      {event.booking_url && (
                        <a
                          href={event.booking_url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="booking-redirect-link"
                          className="w-full h-12 rounded-full bg-[#1A1A1A] hover:bg-[#FA003F] transition-colors text-white font-bold inline-flex items-center justify-center gap-2"
                        >
                          <ExternalLink className="h-4 w-4" /> Book on Partner Site
                        </a>
                      )}
                      {event.contact_number && (
                        <a
                          href={`tel:${event.contact_number.replace(/\s+/g, "")}`}
                          data-testid="booking-contact-link"
                          className="w-full h-12 rounded-full border border-[#FA003F] text-[#FA003F] hover:bg-[#FA003F] hover:text-white transition-colors font-bold inline-flex items-center justify-center gap-2"
                          onClick={() => toast.success("Opening dialer…")}
                        >
                          <Phone className="h-4 w-4" /> Call {event.contact_number}
                        </a>
                      )}
                    </div>
                  )}
                </div>

                <div className="bg-[#1A1A1A] text-white rounded-3xl p-6">
                  <div className="text-xs font-bold uppercase tracking-widest text-white/60">Interested going</div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <div className="font-display text-4xl font-black">{event.interests_count || 0}</div>
                    <div className="text-white/60 text-sm">people</div>
                  </div>
                  <div className="mt-2 text-sm text-white/70">Grab your spot before it fills up.</div>
                </div>

                <button
                  data-testid="sponsor-open-btn"
                  onClick={() => setSponsorOpen(true)}
                  className="w-full text-left rounded-3xl p-6 bg-gradient-to-br from-[#FA003F] to-[#C40031] text-white overflow-hidden relative group"
                >
                  <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10 group-hover:scale-110 transition-transform" />
                  <div className="relative">
                    <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-white/80">
                      <Rocket className="h-3.5 w-3.5" /> Organizer perk
                    </div>
                    <div className="font-display text-2xl font-black mt-2 leading-tight">
                      {event.is_sponsored ? "Extend the boost" : "Boost this event"}
                    </div>
                    <div className="text-sm text-white/85 mt-1">
                      {event.is_sponsored ? "Add more days at the top of the hero." : "Pin it to the hero. Get more eyeballs. From ₹499."}
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-sm font-bold mt-4 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full">
                      See tiers <Sparkles className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </button>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <OtpDialog open={otpOpen} onOpenChange={setOtpOpen} onVerified={handleVerified} />
      <SponsorDialog
        open={sponsorOpen}
        onOpenChange={setSponsorOpen}
        eventId={event.id}
        onSponsored={() => eventApi.get(id).then(setEvent)}
      />
    </div>
  );
}
